import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from "@angular/forms";
import {
  AccountTransferService,
  AppBarAction,
  AppBarActionsService,
  AppBarTitleService,
  AppPageService, InitiatorType,
  PromotionInfoData,
  ProductCode,
  MessageService
} from "common";
import { ActivatedRoute } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

import { SimulationTransactionData } from "../../transaction.model";
import { UserData } from "../../../user/user.model";
import { LoanService } from "../../../loan/loan.service";
import { UserService } from "../../../user/user.service";

@UntilDestroy()
@Component({
    selector: 'ifb-transaction-simulation-payment',
    templateUrl: './transaction-simulation-payment.component.html',
    styleUrls: ['./transaction-simulation-payment.component.scss'],
    standalone: false
})
export class TransactionSimulationPaymentComponent implements OnInit {
  form: UntypedFormGroup;
  data: SimulationTransactionData;
  currentUser: UserData;
  promotionInfo: PromotionInfoData;

  protected maximumAmount: number;
  protected minimumAmount: number;
  protected paymentFrequency: string;
  readonly ProductCode = ProductCode;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly loanService: LoanService,
    private readonly accountTransferService: AccountTransferService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly appPageService: AppPageService,
    private readonly service: UserService,
    private readonly messageService: MessageService,
  ) {
    this.formInit();
  }

  ngOnInit(): void {
    this.service.current
      .subscribe((user: UserData | null) => {
        this.currentUser = user;
      });

    this.route.data
      .pipe(untilDestroyed(this))
      .subscribe((data: { transactionDetails: SimulationTransactionData }) => {
        this.data = data.transactionDetails;
        this.paymentFrequency =
          this.accountTransferService.getLoanOfferPaymentFrequencyLabel(
            this.data.paymentFrequency
          );
        this.setMinimumAmount();
        this.setMaximumAmount();
        this.form.controls.paymentAmount.setValidators(
          this.getPaymentAmountValidators()
        );
      });

    this.appBarTitleService.title = "Simulate a payment";

    this.form.controls.paymentAmount.valueChanges
      .pipe(untilDestroyed(this), debounceTime(500), distinctUntilChanged())
      .subscribe((value: string) => {
        this.data.amountInput = value;
        this.accountNewBalancesInit();
      });

    this.appBarActionsService.invoking
      .pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));
  }

  setAmount(value: number): void {
    this.form.controls.paymentAmount.setValue(value);
  }

  close(): void {
    this.appPageService.back();
  }

  applyPromotionChanged(promotion: PromotionInfoData): void {
    this.promotionInfo = promotion;
    this.setMinimumAmount();
    this.setMaximumAmount();

    this.form.controls.paymentAmount.setValidators(
      this.getPaymentAmountValidators()
    );
    this.form.controls.paymentAmount.updateValueAndValidity();
    if (this.promotionInfo?.payoffAmount)
      this.form.controls.paymentAmount.markAsTouched();
    this.accountNewBalancesInit();
  }

  private setMaximumAmount(): void {
    this.maximumAmount =
      this.promotionInfo?.payoffAmount ??
      this.data.amountMaximum;
  }

  private setMinimumAmount(): void {
    this.minimumAmount = Math.min(
      this.data.amountMinimum,
      this.promotionInfo?.payoffAmount ?? this.data.amountMinimum);
  }

  private formInit(): void {
    this.form = this.formBuilder.group({
      paymentAmount: [''],
    });
    this.setAppBarActions();
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void =
      this[action.id].bind(this);
    actionHandler(action);
  }

  private accountNewBalancesInit(): void {
    const value = this.data.amountInput
      ? parseFloat(this.data.amountInput)
      : 0;

    this.loanService
      .payPreview(
        this.data.accountId,
        value,
        InitiatorType.Customer,
        this.currentUser.id.toString(),
        this.promotionInfo?.promoCode)
      .subscribe((res) => {
          // Reassign a new object reference so ifc-transaction-details (which
          // recomputes its list only in ngOnChanges) re-renders. Mutating the
          // existing object in place does not change the @Input reference and
          // leaves the panel stale.
          this.data.accountTransferData = {
            ...this.data.accountTransferData,
            balanceAfter: {
              ...this.data.accountTransferData.balanceAfter,
              outstandingBalance: res.newOutstandingBalance,
              availableBalance: res.newAvailableFunds,
              upcomingPaymentAmount: res.newUpcomingPaymentAmount,
              upcomingPaymentDate: res.newUpcomingPaymentDate,
            },
            promotionInfo: res.promotionInfo,
          };
        },
        error => this.messageService.error(error));
  }

  private getPaymentAmountValidators(): ValidatorFn[] {
    return [
      Validators.required,
      Validators.min(this.minimumAmount),
      Validators.max(this.maximumAmount),
    ];
  }

  private setAppBarActions(): void {
    this.appBarActionsService.actions = [];
    this.appBarActionsService.actions.push({
      id: "close",
      label: "CLOSE",
      buttonType: "button",
    });
  }
}
